/**
 * Challenges this device took: one small record per challenge code, written when a
 * challenge game first finishes, so Stats can say how you do against challengers.
 * `arrow:v1:challenges:taken` = `{ v: 1, items: TakenChallenge[] }`, parsed defensively
 * (a bad blob reads as none, a bad item is skipped). Anonymous: a code, the puzzle and
 * two times, never a name. Never throws.
 */
import { isDateKey, isLevel, type Level } from '@arrow/game-engine';

import type { KeyValueStore } from '../storage/kv.ts';
import { platformStore } from './config.ts';

export const TAKEN_KEY = 'arrow:v1:challenges:taken';

/** The most records kept (oldest dropped). Small enough to ride in the carry snapshot. */
export const MAX_TAKEN = 100;

export interface TakenChallenge {
	code: string;
	number: number;
	level: Level;
	/** The daily's date (`YYYY-MM-DD`). */
	date: string;
	/** Your finishing time, ms. */
	yourMs: number;
	/** The challenger's time, ms. */
	theirMs: number;
	/** You were strictly faster (as the api counts it). */
	beat: boolean;
	/** When you finished (ISO). */
	finishedAt: string;
}

const ms = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0;

function isTaken(v: unknown): v is TakenChallenge {
	if (typeof v !== 'object' || v === null) return false;
	const c = v as Record<string, unknown>;
	return (
		typeof c.code === 'string' &&
		c.code.length > 0 &&
		c.code.length <= 16 &&
		typeof c.number === 'number' &&
		Number.isInteger(c.number) &&
		isLevel(c.level) &&
		typeof c.date === 'string' &&
		isDateKey(c.date) &&
		ms(c.yourMs) &&
		ms(c.theirMs) &&
		typeof c.beat === 'boolean' &&
		typeof c.finishedAt === 'string' &&
		!Number.isNaN(Date.parse(c.finishedAt))
	);
}

/** Only the known fields, so nothing else (a name, say) ever rides along. */
const clean = (c: TakenChallenge): TakenChallenge => ({
	code: c.code,
	number: c.number,
	level: c.level,
	date: c.date,
	yourMs: c.yourMs,
	theirMs: c.theirMs,
	beat: c.beat,
	finishedAt: c.finishedAt,
});

/** Parses a raw stored blob: the valid records, first per code, oldest first. */
export function parseTaken(raw: string | null): TakenChallenge[] {
	if (raw === null) return [];
	try {
		const parsed = JSON.parse(raw) as { v?: unknown; items?: unknown } | null;
		if (parsed?.v !== 1 || !Array.isArray(parsed.items)) return [];
		const seen = new Set<string>();
		const out: TakenChallenge[] = [];
		for (const item of parsed.items) {
			if (!isTaken(item) || seen.has(item.code)) continue;
			seen.add(item.code);
			out.push(clean(item));
		}
		return out.slice(-MAX_TAKEN);
	} catch {
		return [];
	}
}

export function serializeTaken(items: readonly TakenChallenge[]): string {
	return JSON.stringify({ v: 1, items: items.slice(-MAX_TAKEN).map(clean) });
}

export function challengesTaken(store: KeyValueStore = platformStore): TakenChallenge[] {
	try {
		return parseTaken(store.get(TAKEN_KEY));
	} catch {
		return [];
	}
}

/**
 * Records a finished challenge, first finish only: a code already recorded is left as
 * it was. Returns whether it wrote. Never throws.
 */
export function recordChallengeTaken(c: TakenChallenge, store: KeyValueStore = platformStore): boolean {
	try {
		if (!isTaken(c)) return false;
		const list = challengesTaken(store);
		if (list.some((t) => t.code === c.code)) return false;
		return store.set(TAKEN_KEY, serializeTaken([...list, c]));
	} catch {
		return false;
	}
}

export interface TakenTally {
	taken: number;
	beat: number;
}

export function tallyTaken(items: readonly TakenChallenge[]): TakenTally {
	return { taken: items.length, beat: items.filter((t) => t.beat).length };
}
